import * as XLSX from 'xlsx';

/**
 * Format bone movement data untuk Excel
 * @param {Object} movementRules - Object berisi movement rules
 * @param {Object} maxAngleLimits - Object berisi max angle limits
 * @returns {string} - Formatted string untuk Excel
 */
const formatBoneData = (movementRules = {}, maxAngleLimits = {}) => {
  const boneEntries = [];
  
  // Gabungkan movement rules dan max angle limits
  const allBones = new Set([
    ...Object.keys(movementRules),
    ...Object.keys(maxAngleLimits)
  ]);
  
  allBones.forEach(bone => {
    const rule = movementRules[bone];
    const limit = maxAngleLimits[bone];
    
    if (rule && limit) {
      // Format: "move rThight 20" atau "Fixed spine"
      let action;
      if (rule === 'move') {
        // Handle array limits (e.g., [20, 40]) or single values
        const limitValue = Array.isArray(limit) ? limit[0] : limit;
        action = `move ${bone} ${limitValue}`;
      } else if (rule === 'fixed') {
        action = `Fixed ${bone}`;
      } else {
        action = `${rule} ${bone}`;
      }
      boneEntries.push(action);
    } else if (rule) {
      const action = rule === 'move' ? `move ${bone}` : 
                    rule === 'fixed' ? `Fixed ${bone}` : 
                    `${rule} ${bone}`;
      boneEntries.push(action);
    } else if (limit) {
      const limitValue = Array.isArray(limit) ? limit[0] : limit;
      boneEntries.push(`move ${bone} ${limitValue}`);
    }
  });
  
  return boneEntries.join(', ');
};

/**
 * Format QA status untuk Excel
 * @param {string} qaStatus - QA status dari database
 * @returns {string} - Formatted status untuk Excel
 */
const formatQAStatus = (qaStatus) => {
  switch (qaStatus) {
    case 'passed':
      return 'QA Passed';
    case 'wip':
      return 'Work in progress';
    case 'new':
    default:
      return 'New Exercise';
  }
};

/**
 * Export exercise data ke Excel
 * @param {Array} exercises - Array of exercise data
 * @param {Object} statusCounts - Object berisi count per status
 */
export const exportExercisesToExcel = (exercises, statusCounts) => {
  // Prepare data untuk Excel
  const excelData = [];
  
  // Header row 1
  const headerRow1 = [
    'Exercise ID',
    '', // Empty cell untuk alignment
    'Bones Reference Parameters',
    '', // Empty untuk merged cell
    '', // Empty untuk merged cell
    'QA status',
    'Quantity Passed',
    'Quantity WIP',
    'Quantity New',
    'Total Exercise'
  ];
  
  // Header row 2
  const headerRow2 = [
    '', // Empty untuk Exercise ID
    '', // Empty cell
    'Begin',
    'Midle',
    'End',
    '', // Empty untuk QA status
    '', // Empty untuk quantity columns
    '', // Empty untuk quantity columns
    '', // Empty untuk quantity columns
    ''  // Empty untuk quantity columns
  ];
  
  excelData.push(headerRow1);
  excelData.push(headerRow2);
  
  // Data rows
  exercises.forEach((exercise, index) => {
    const row = [
      exercise.id || exercise._id,
      '', // Empty cell
      '', // Begin - akan diisi dari referenceMotionData
      '', // Midle - akan diisi dari referenceMotionData
      '', // End - akan diisi dari referenceMotionData
      formatQAStatus(exercise.qaStatus),
      '', // Quantity Passed - kosong sesuai gambar
      '', // Quantity WIP - kosong sesuai gambar
      '', // Quantity New - kosong sesuai gambar
      ''  // Total Exercise - kosong sesuai gambar
    ];
    
    // Isi data bones reference parameters jika ada
    if (exercise.referenceMotionData && Array.isArray(exercise.referenceMotionData)) {
      const phases = exercise.referenceMotionData;
      
      // Cari phase berdasarkan nama
      const beginPhase = phases.find(p => p.name === 'begin');
      const middlePhase = phases.find(p => p.name === 'middle');
      const endPhase = phases.find(p => p.name === 'end');
      
      if (beginPhase) {
        row[2] = formatBoneData(beginPhase.movementRules, beginPhase.maxAngleLimits);
      }
      if (middlePhase) {
        row[3] = formatBoneData(middlePhase.movementRules, middlePhase.maxAngleLimits);
      }
      if (endPhase) {
        row[4] = formatBoneData(endPhase.movementRules, endPhase.maxAngleLimits);
      }
    }
    
    excelData.push(row);
  });
  
  // Buat workbook
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(excelData);
  
  // Set column widths
  const colWidths = [
    { wch: 12 }, // Exercise ID
    { wch: 2 },  // Empty
    { wch: 30 }, // Begin
    { wch: 30 }, // Midle
    { wch: 30 }, // End
    { wch: 15 }, // QA status
    { wch: 15 }, // Quantity Passed
    { wch: 12 }, // Quantity WIP
    { wch: 12 }, // Quantity New
    { wch: 15 }  // Total Exercise
  ];
  ws['!cols'] = colWidths;
  
  // Merge cells untuk header
  ws['!merges'] = [
    { s: { r: 0, c: 2 }, e: { r: 0, c: 4 } } // Merge "Bones Reference Parameters"
  ];
  
  // Apply styling (basic styling - Excel akan handle sebagian besar)
  const range = XLSX.utils.decode_range(ws['!ref']);
  
  // Style header cells
  for (let R = 0; R <= 1; ++R) {
    for (let C = 0; C <= 9; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
      
      // Set background colors sesuai gambar
      if (R === 0) {
        if (C === 0) {
          // Exercise ID - dark gray background
          ws[cellAddress].s = { fill: { fgColor: { rgb: "404040" } }, font: { color: { rgb: "FFFFFF" } } };
        } else if (C >= 2 && C <= 4) {
          // Bones Reference Parameters - light yellow background
          ws[cellAddress].s = { fill: { fgColor: { rgb: "FFFF99" } } };
        } else if (C === 5) {
          // QA status - solid yellow background
          ws[cellAddress].s = { fill: { fgColor: { rgb: "FFFF00" } } };
        } else {
          // Quantity columns - white background
          ws[cellAddress].s = { fill: { fgColor: { rgb: "FFFFFF" } } };
        }
      } else if (R === 1) {
        if (C >= 2 && C <= 4) {
          // Begin, Midle, End - light yellow background
          ws[cellAddress].s = { fill: { fgColor: { rgb: "FFFF99" } } };
        }
      }
    }
  }
  
  // Style data cells - Exercise ID dengan light green background
  for (let R = 2; R <= range.e.r; ++R) {
    const cellAddress = XLSX.utils.encode_cell({ r: R, c: 0 });
    if (ws[cellAddress]) {
      ws[cellAddress].s = { fill: { fgColor: { rgb: "90EE90" } } };
    }
  }
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Exercise Data');
  
  // Generate filename dengan timestamp
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const filename = `exercise-data-${timestamp}.xlsx`;
  
  // Export file
  XLSX.writeFile(wb, filename);
  
  console.log(`Excel file exported: ${filename}`);
  return filename;
};
